import moment from "moment";

/**
 * Parses a date string as a local date (not UTC)
 * This prevents the "one day earlier" bug when dates come from the backend
 * as strings like "2025-12-15" without time information.
 * 
 * @param {string|Date} dateInput - Date string (e.g., "2025-12-15") or Date object
 * @returns {Date} - Date object in local timezone
 */
export const parseLocalDate = (dateInput) => {
  if (!dateInput) return null;
  
  // If it's already a Date object, return it
  if (dateInput instanceof Date) {
    return dateInput;
  }
  
  // If it's a string, parse it as local date
  if (typeof dateInput === "string") {
    // Check if it's a date-only string (YYYY-MM-DD format)
    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
    
    if (dateOnlyPattern.test(dateInput.trim())) {
      // Parse as local date to avoid UTC conversion issues
      const [year, month, day] = dateInput.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    
    // For other date strings, use moment to parse and convert to local
    const momentDate = moment(dateInput);
    if (momentDate.isValid()) {
      return momentDate.toDate();
    }
  }
  
  // Fallback to moment parsing
  const momentDate = moment(dateInput);
  if (momentDate.isValid()) {
    return momentDate.toDate();
  }
  
  return null;
};

/**
 * Parses a date string and ensures it's treated as a local date for calendar events
 * This is specifically for calendar events where dates should be displayed
 * on the correct day regardless of timezone.
 * 
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date} - Date object suitable for calendar display
 */
export const parseCalendarDate = (dateInput) => {
  return parseLocalDate(dateInput);
};

/**
 * Formats a date string with time using the user's TimeFormat setting
 * Similar to POS component's formatDate function
 * 
 * @param {string|Date} dateString - Date string or Date object to format
 * @param {string} dateFormat - Date format string (e.g., "MM/DD/YYYY")
 * @param {string} timeFormat - Time format setting ("12h" or "24h")
 * @param {boolean} includeSeconds - Whether to include seconds in time format (default: true)
 * @returns {string} - Formatted date string with time
 */
export const formatDateWithTime = (dateString, dateFormat = "MM/DD/YYYY", timeFormat = "12h", includeSeconds = true) => {
  if (!dateString) return "-";
  
  try {
    // Add time format to date format
    let displayFormat = String(dateFormat);

    // DO NOT replace slashes with dashes - preserve the user's chosen separator (/, -, etc.)
    // displayFormat = displayFormat.replace(/\//g, "-"); // REMOVED to preserve "/" separator

    // Strip existing time formats to prevent duplicates
    displayFormat = displayFormat
      .replace(/\s*h{1,2}:mm\s*[aApP][mM]\s*/gi, " ") // 12-hour format
      .replace(/\s*H{1,2}:mm\s*/gi, " ") // 24-hour format
      .replace(/\s+[aApP][mM]?\s*$/gi, " ") // Remove trailing AM/PM
      .replace(/\s+[aApP][mM]?\s+/gi, " ") // Remove standalone AM/PM
      .replace(/\s+/g, " ")
      .trim();

    // Add time format based on user's time format setting
    // timeFormat can be "12h" or "24h" (from TimeFormat settings)
    const timeFormatString = includeSeconds 
      ? (timeFormat === "24h" ? "H:mm:ss" : "h:mm:ss A")
      : (timeFormat === "24h" ? "H:mm" : "h:mm A");
    displayFormat = displayFormat + " " + timeFormatString;

    // Use local time directly without timezone conversion to match what user sees
    return moment(dateString).local().format(displayFormat);
  } catch (error) {
    console.error("Error formatting date:", error);
      // Fallback uses configured time format
      const fallbackTimeFormat = includeSeconds
        ? (timeFormat === "24h" ? "H:mm:ss" : "h:mm:ss A")
        : (timeFormat === "24h" ? "H:mm" : "h:mm A");
    return moment(dateString).local().format(`${dateFormat} ${fallbackTimeFormat}`);
  }
};

